import { useEffect } from "react"
import NextLink from "next/link"
import { useRouter } from "next/router"
import {
  Box,
  Input,
  FormLabel,
  Button,
  Link,
  Center,
  Text,
  Heading,
  FormControl,
  FormErrorMessage,
  useToast,
  Checkbox,
} from "@chakra-ui/react"
import * as R from "ramda"
import { useForm } from "react-hook-form"
import { useTranslations } from "use-intl"
import { PageSEO } from "@components/HeadSEO"
import { useUser } from "@contexts/userContext"
import supabase from "@utils/initSupabase"

const SignUp = (): JSX.Element => {
  const { register, handleSubmit, formState } = useForm()
  const { user } = useUser()
  const router = useRouter()
  const toast = useToast()
  const t = useTranslations()

  const handleSignUp = async ({
    name,
    email,
    password,
    marketingPermission,
  }: {
    name?: string
    email?: string
    password?: string
    marketingPermission?: boolean
  }) => {
    const { user } = await supabase.auth.signUp({
      email,
      password,
    })

    if (!R.isNil(user)) {
      await supabase
        .from("users")
        .update({
          full_name: name,
          marketing_permission: marketingPermission,
          email,
        })
        .eq("id", user?.id)

      const { user: signedInUser } = await supabase.auth.signIn(
        {
          email,
          password,
        },
        { redirectTo: window.location.origin }
      )

      toast({
        title: t("signup.toastSuccessTitle", { name }),
        description: t("signup.registrationSuccessful"),
        status: "success",
        duration: 5000,
        isClosable: true,
      })

      // Only sign up to newsletters if user account is created successfully
      if (!R.isNil(signedInUser) && marketingPermission) {
        await fetch(
          `/api/newsletters/subscribe?name=${name}&email=${email}&tags=site_signup`,
          {
            headers: new Headers({ "Content-Type": "application/json" }),
            credentials: "same-origin",
          }
        )
      }
    } else {
      toast({
        title: t("signup.toastErrorTitle"),
        description: t("signup.registrationNotSuccessful"),
        status: "error",
        duration: 9000,
        isClosable: true,
      })
    }
  }

  useEffect(() => {
    if (user) {
      router.push("/")
    }
  }, [user, router])

  return (
    <>
      <PageSEO
        title={String(t("signup.pageTitle"))}
        description={String(t("signup.pageDescription"))}
      />
      <Center>
        <Box
          maxW="30rem"
          w="100%"
          mt={[6, null, 12, 16, 20]}
          p="6"
          overflow="hidden"
        >
          <Heading variant="contentHeading1">
            {t("signup.createAccount")}
          </Heading>
          <Text mt="4">{t("signup.createAccountDescription")}</Text>
          <form onSubmit={handleSubmit(handleSignUp)}>
            <FormControl isInvalid={!!formState.errors.name}>
              <FormLabel htmlFor="name" mt="4">
                {t("signup.formNameLabel")}
                <Box display="inline" color="red">
                  *
                </Box>
              </FormLabel>
              <Input
                id="name"
                type="text"
                placeholder={String(t("signup.formNamePlaceholder"))}
                focusBorderColor="orange"
                required
                {...register("name", {
                  minLength: {
                    value: 6,
                    message: String(t("signup.formNameErrorMsg")),
                  },
                })}
              />
              <FormErrorMessage color="white">
                {typeof formState.errors.name?.message === "string"
                  ? formState.errors.name.message
                  : null}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!formState.errors.email}>
              <FormLabel htmlFor="email" mt="4">
                {t("signup.formEmailLabel")}
                <Box display="inline" color="red">
                  *
                </Box>
              </FormLabel>
              <Input
                id="email"
                type="email"
                placeholder={String(t("signup.formEmailLabel"))}
                focusBorderColor="orange"
                required
                {...register("email", {
                  pattern: {
                    value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, // eslint-disable-line
                    message: String(t("signup.formEmailErrorMsg")),
                  },
                })}
              />
              <FormErrorMessage color="white">
                {typeof formState.errors.email?.message === "string"
                  ? formState.errors.email.message
                  : null}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!formState.errors.password}>
              <FormLabel htmlFor="password" mt="4">
                {t("signup.formPasswordLabel")}
                <Box display="inline" color="red">
                  *
                </Box>
              </FormLabel>
              <Input
                id="password"
                type="password"
                required
                placeholder={String(t("signup.formPasswordPlaceholder"))}
                focusBorderColor="orange"
                {...register("password", {
                  minLength: {
                    value: 6,
                    message: String(t("signup.formPasswordErrorMsg")),
                  },
                })}
              />
              <FormErrorMessage color="white">
                {typeof formState.errors.password?.message === "string"
                  ? formState.errors.password.message
                  : null}
              </FormErrorMessage>
            </FormControl>

            <FormControl>
              <Checkbox
                id="marketingPermission"
                colorScheme="orange"
                mt="4"
                {...register("marketingPermission")}
              >
                {t("account.marketingPermissionDescription")}
              </Checkbox>
            </FormControl>

            <FormControl isInvalid={!!formState.errors.acceptedToS}>
              <Checkbox
                id="acceptedToS"
                colorScheme="orange"
                mt="4"
                required
                {...register("acceptedToS", {
                  required: true,
                })}
              >
                {t("signup.userAccepts")}{" "}
                <a
                  href="https://www.chefs.live/kayttoehdot"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("signup.tos")}
                </a>
              </Checkbox>
              <FormErrorMessage color="white">
                {!!formState.errors.acceptedToS &&
                  t("signup.formFieldRequired")}
              </FormErrorMessage>
            </FormControl>

            <Button
              type="submit"
              size="md"
              w="100%"
              mt="8"
              disabled={formState.isSubmitting}
            >
              {formState.isSubmitting
                ? t("signup.submitLoading")
                : t("signup.submit")}
            </Button>
            <NextLink href={String(t("navigation.signInUrl"))} passHref>
              <Link textAlign="center" mt="4" display="block">
                {t("signup.signInMsg")}
              </Link>
            </NextLink>
          </form>
        </Box>
      </Center>
    </>
  )
}

export default SignUp
